import { useState, useEffect } from "react";
import { useTimeout } from "@mantine/hooks";
import { AutoTableRowExpansionProps } from "./auto-table.types";

/**
 * Hook managing the expansion state of a row
 * @param rowExpansion Configuration for row expansion
 * @param records The records displayed in the table
 * @param getKey Function to get the unique key of a record
 * @returns An object containing methods and properties to manage row expansion
 */
export function useRowExpansion<T>({
    rowExpansion,
    records,
    getKey,
}: {
    rowExpansion?: AutoTableRowExpansionProps<T>;
    records: T[] | undefined;
    getKey: (row: T) => string | number;
}) {
    let initiallyExpandedRecordIds: unknown[] = [];
    if (rowExpansion && records) {
        const { trigger, allowMultiple, initiallyExpanded } = rowExpansion;
        if (records && trigger === "always") {
            initiallyExpandedRecordIds = records.map((r) => getKey(r));
        } else if (initiallyExpanded) {
            initiallyExpandedRecordIds = records
                .filter((record, index) => initiallyExpanded({ record, index }))
                .map((r) => getKey(r));
            if (!allowMultiple) {
                initiallyExpandedRecordIds = [initiallyExpandedRecordIds[0]];
            }
        }
    }

    let expandedRecordIds: unknown[];
    let setExpandedRecordIds: ((expandedRecordIds: unknown[]) => void) | undefined;
    const expandedRecordIdsState = useState<unknown[]>(initiallyExpandedRecordIds);

    if (rowExpansion) {
        const {
            expandable,
            trigger,
            allowMultiple,
            collapseProps,
            content,
            showExpandChevron,
            expandedRowProps,
            cellProps,
            contentProps,
        } = rowExpansion;
        const expandOnClick = trigger !== "always" && trigger !== "never";
        if (rowExpansion.expanded) {
            ({ recordIds: expandedRecordIds, onRecordIdsChange: setExpandedRecordIds } = rowExpansion.expanded);
        } else {
            [expandedRecordIds, setExpandedRecordIds] = expandedRecordIdsState;
        }

        const collapseRow = (record: T) =>
            setExpandedRecordIds?.(expandedRecordIds.filter((id) => id !== getKey(record)));

        return {
            expandOnClick,
            showExpandChevron: !!showExpandChevron && expandOnClick,
            expandedRowProps,
            cellProps,
            contentProps,
            isRowExpanded: (record: T) => (trigger === "always" ? true : expandedRecordIds.includes(getKey(record))),
            isExpandable: ({ record, index }: { record: T; index: number }) => {
                if (!expandable) {
                    return true;
                }
                return expandable({ record, index });
            },
            expandRow: (record: T) => {
                const recordId = getKey(record);
                setExpandedRecordIds?.(allowMultiple ? [...expandedRecordIds, recordId] : [recordId]);
            },
            collapseRow,
            collapseProps,
            content:
                ({ record, index }: { record: T; index: number }) =>
                () =>
                    content({ record, index, collapse: () => collapseRow(record) }),
        };
    }
}

/**
 * Hook managing the expansion state of a row
 * @param open Whether the row is open or not
 * @param transitionDuration Duration of the transition in milliseconds
 * @returns An object containing the `expanded` and `visible` states
 */
export function useRowExpansionStatus(open: boolean, transitionDuration?: number) {
    const [expanded, setExpanded] = useState(open);
    const [visible, setVisible] = useState(open);

    const expand = useTimeout(() => setExpanded(true), 0);
    const hide = useTimeout(() => setVisible(false), transitionDuration || 200);

    useEffect(() => {
        if (open) {
            hide.clear();
            setVisible(true);
            expand.start();
        } else {
            expand.clear();
            setExpanded(false);
            hide.start();
        }
    }, [expand, hide, open]);

    return { expanded, visible };
}
